import { collection, doc, setDoc, writeBatch } from 'firebase/firestore';
import { IDateRange } from '../../../providers/settings/dates/dateRangeProvider';
import { IBlockReportSettings } from '../../../providers/settings/reports/blockReportSettingsProvider';
import { ISettingsTemplate } from '../../../providers/settings/reports/types';
import { userAsPromise } from '../../../providers/userProvider';

/**
 * @description updates the block template
 * @param metric
 * @returns Promise
 */
export const updateBlockTemplate = async (
  template: ISettingsTemplate<IBlockReportSettings>,
  isSelected: boolean
): Promise<void> => {
  const user = await userAsPromise();

  if (isSelected) {
    const batch = writeBatch(user.services.firestore);
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/blockReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/blockReport`
      ),
      { selected: template },
      { merge: true }
    );
    await batch.commit();
  } else {
    await setDoc(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/blockReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
  }
};

/**
 * @description creates a new block report settings template
 * @param template
 */

export const createBlockTemplate = async (
  template: Partial<ISettingsTemplate<IBlockReportSettings>>
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    collection(
      user.services.firestore,
      `userProfiles/${user.id}/settings/blockReport/templates`
    )
  );
  const newDocument = {
    ...template,
    id: docRef.id,
    active: true,
  };
  await setDoc(docRef, newDocument, { merge: true });
};

/**
 * @description saves the template as the selected template
 * @param template
 */
export const saveBlockSettings = async (
  template: ISettingsTemplate<IBlockReportSettings>,
  timeframe: IDateRange,
  isMaxDatapoints: boolean
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/blockReport`
  );

  await setDoc(
    docRef,
    {
      selected: template ?? null,
      timeframe: timeframe ?? null,
      isMaxDatapoints: isMaxDatapoints ?? null,
    },
    { merge: true }
  );
};

/**
 * @description delete the template and change the current template to another one
 * @param template
 * @param replacement
 * @returns
 */
export const deleteSelectedTemplate = async (
  template: ISettingsTemplate<IBlockReportSettings>,
  replacement: ISettingsTemplate<IBlockReportSettings>
): Promise<void> => {
  const user = await userAsPromise();

  const batch = writeBatch(user.services.firestore);
  const updateRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/blockReport/templates/${template.id}`
  );
  const update: ISettingsTemplate<IBlockReportSettings> = {
    ...template,
    active: false,
  };
  batch.update(updateRef, update as unknown as Record<string, unknown>);
  const selectedRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/blockReport`
  );
  batch.set(selectedRef, { selected: replacement ?? null }, { merge: true });
  return batch.commit();
};
